import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { QmAssociationChange } from 'src/app/shared/models/qm-association.model';
import { BulkQmAssociationAssignmentChange } from 'src/app/shared/models/user-assignment-change-request.model';
import { ClientPortalVisibilitySettings } from '../../models/client-portal-visibility-settings.model';
import { ClientPortalVisibilityUpdate } from '../../models/client-portal-visibility-update.model';
import { DocDueDate } from '../../models/doc-due-date.model';
import { ProfileRequirement } from '../../models/profile-requirements.model';
import { QmAssociationAutoMatchSettingsViewModel as LegacyQmAssociationAutoMatchSettingsViewModel } from '../../models/qm-association-auto-match-settings-dto.model';
import { QmAssociationGridItem } from '../../models/qm-association.model';
import { QmAssociationService } from '../../qm-associations/qm-association.service';
import { CategoryLineOfBusinessEnum } from '../enums/category-line-of-business-enum.enum';
import { LineOfBusinessEnum } from '../enums/line-of-business-enum.enum';
import { BulkAssignmentsRequestDto } from '../models/bulk-assignments-request-dto.model';
import { ClientPortalVisibilitySettingsDto } from '../models/client-portal-visibility-settings-dto.model';
import { ClientPortalVisibilityUpdateDto } from '../models/client-portal-visibility-update-dto.model';
import { DocDueDateDto } from '../models/doc-due-date-dto.model';
import { PagingTokenOfQmAssociationGridDto } from '../models/paging-token-of-qm-association-grid-dto.model';
import { ProfileRequirementsDto } from '../models/profile-requirements-dto.model';
import { ProfileRequirementsFilterDto } from '../models/profile-requirements-filter-dto.model';
import { QmAssociationAutoMatchSettingsViewModel } from '../models/qm-association-auto-match-settings-view-model.model';
import { QmAssociationDto } from '../models/qm-association-dto.model';
import { QmAssociationFilterDto } from '../models/qm-association-filter-dto.model';
import { QmAssociationRequestDto } from '../models/qm-association-request-dto.model';
import { QmAssociationWithClDto } from '../models/qm-association-with-cl-dto.model';
import { QmAssociationsClient } from '../qm-associations-client.service';
import { NSwagMigrationService } from './nswag-migration.service';
type GetQmAssociationRequirementsParams = Parameters<typeof QmAssociationsClient.prototype.getQmAssociationRequirements>;

// 15 endpoints
@Injectable({ providedIn: 'root' })
export class QmAssociationNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'QmAssociationNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationQmAssociationService;
    private readonly legacy = inject(QmAssociationService);
    private readonly qmAssociationsClient = inject(QmAssociationsClient);

    // ** QmAssociationsController.QmAssociations
    getQmAssociations(args: FetchArgs): Observable<PagingTokenFixed<QmAssociationGridItem[]> | PagingTokenOfQmAssociationGridDto> {
        const filter: QmAssociationFilterDto = args.filter ?? {};

        return this.invoke(x => x
            ? this.legacy.getQmAssociations(args)
            : this.qmAssociationsClient.qmAssociations(
                filter.id, // filter_Id
                filter.name, // filter_Name
                filter.clName, // filter_ClName
                filter.mappingStatus, // filter_MappingStatus
                filter.autoMatchStrategy, // filter_AutoMatchStrategy
                args.pageSize, // pageSize
                args.skip, // skip
                args.sort?.field, // sort_Field
                args.sort?.dir as any, // sort_Type
                undefined // paginated
            ));
    }

    // ** QmAssociationsController.LookupQmAssociations
    getAssociations(lookUpParm: string): Observable<KeyValue<number, string>[] | QmAssociationDto[]> {
        return this.invoke(x => x
            ? this.legacy.getAssociations(lookUpParm)
            : this.qmAssociationsClient.lookupQmAssociations(lookUpParm));
    }

    // ** QmAssociationsController.GetQmAssociationRequirements
    getQmAssociationRequirements(
        id: number,
        lineOfBusiness: LineOfBusiness[] | LineOfBusinessEnum[],
        args: FetchArgs
    ): Observable<ProfileRequirement[] | ProfileRequirementsDto[]> {
        const { legacy, featureManager, qmAssociationsClient } = this;
        const filter: ProfileRequirementsFilterDto = args.filter ?? {};

        return this.invoke(x => {
            if (x) {
                return legacy.getQmAssociationRequirements(id, lineOfBusiness as LineOfBusiness[], args);
            }

            // Normalize
            const normalizedLobs = (lineOfBusiness as LineOfBusinessEnum[]).map(lob =>
                lob === LineOfBusinessEnum.Locums || lob === LineOfBusinessEnum.AccessControl ? lob : LineOfBusinessEnum.Travel);
                
            // Create parameters
            const params: GetQmAssociationRequirementsParams = [
                id,
                normalizedLobs.map(this.convertToClob),// replace FLOB with CLOB,
                filter.requirementId, // filter_RequirementId
                filter.requirementName, // filter_RequirementName
                filter.taskType, // filter_TaskType
                filter.clientPortalVisibility, // filter_ClientPortalVisibility
                filter.requirementLevel, // filter_RequirementLevel
                filter.qualificationSettingsLevel, // filter_QualificationSettingsLevel
                filter.linesOfBusiness, // filter_LinesOfBusiness
                filter.isDefault, // filter_IsDefault
                filter.expirationRequired, // filter_ExpirationRequired
                filter.requirementDescription, // filter_RequirementDescription
                filter.externalRequirementDescription, // filter_ExternalRequirementDescription
                filter.categoryLineOfBusinessId, // filter_CategoryLineOfBusinessId
                filter.acceptanceCriteriaType, // filter_AcceptanceCriteriaType
                filter.acceptanceCriteriaStatus, // filter_AcceptanceCriteriaStatus
                args.pageSize, // pageSize
                args.skip, // skip
                args.sort?.field, // sort_Field
                args.sort?.dir as any, // sort_Type
                undefined // paginated
            ];
            return qmAssociationsClient.getQmAssociationRequirements(...params as unknown as GetQmAssociationRequirementsParams);         
        });
    }

    // ** QmAssociationsController.GetQmAssociationLineOfBusiness
    getQmAssociationLineOfBusiness(id: number): Observable<LineOfBusiness[] | LineOfBusinessEnum[]> {
        return this.invoke(x => x
            ? this.legacy.getQmAssociationLineOfBusiness(id)
            : this.qmAssociationsClient.getQmAssociationLineOfBusiness(id));
    }

    // ** QmAssociationsController.QmAssociation
    getQmAssociation(id: number): Observable<QmAssociationDto | QmAssociationWithClDto> {
        return this.invoke(x => x
            ? this.legacy.getQmAssociation(id)
            : this.qmAssociationsClient.qmAssociation(id));
    }

    // ** QmAssociationsController.GetClientPortalNetworkVisibility
    getClientPortalVisibility(id: number): Observable<ClientPortalVisibilitySettings | ClientPortalVisibilitySettingsDto> {
        return this.invoke(x => x
            ? this.legacy.getClientPortalVisibility(id)
            : this.qmAssociationsClient.getClientPortalNetworkVisibility(id));
    }

    // ** QmAssociationsController.UpdateClientPortalNetworkVisibility
    updateClientPortalVisibility(id: number, updates: ClientPortalVisibilityUpdate[] | ClientPortalVisibilityUpdateDto[]): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.updateClientPortalVisibility(id, updates)
            : this.qmAssociationsClient.updateClientPortalNetworkVisibility(id.toString(), updates));
    }

    // ** QmAssociationsController.CreateQmAssociation
    createQmAssociation(qmAssociation: QmAssociationChange | QmAssociationRequestDto): Observable<unknown | QmAssociationDto> {
        return this.invoke(x => x
            ? this.legacy.createQmAssociation(qmAssociation)
            : this.qmAssociationsClient.createQmAssociation(qmAssociation));
    }

    // ** QmAssociationsController.GetDocDueDate
    getDocDueDate(qmAssociationId: number): Observable<DocDueDate | DocDueDateDto> {
        return this.invoke(x => x
            ? this.legacy.getDocDueDate(qmAssociationId)
            : this.qmAssociationsClient.getDocDueDate(qmAssociationId));
    }

    // ** QmAssociationsController.UpdateDocDueDate
    updateDocDueDate(qmAssociationId: number, docDueDate: DocDueDate | DocDueDateDto): Observable<unknown> {
        return this.invoke(x => x
            ? this.legacy.updateDocDueDate(qmAssociationId, docDueDate)
            : this.qmAssociationsClient.updateDocDueDate(qmAssociationId, docDueDate));
    }

    // ** QmAssociationsController.UpdateCompleteStatus
    updateCompleteStatus(qmAssociationId: number, isComplete: boolean): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.updateCompleteStatus(qmAssociationId, isComplete)
            : this.qmAssociationsClient.updateCompleteStatus(qmAssociationId, isComplete));
    }

    // ** QmAssociationsController.UpdateQmAssociationAssignments
    updateQmAssociationAssignment(qmAssociationUpdates: BulkQmAssociationAssignmentChange | BulkAssignmentsRequestDto): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.updateQmAssociationAssignment(qmAssociationUpdates)
            : this.qmAssociationsClient.updateQmAssociationAssignments(qmAssociationUpdates));
    }

    // ** QmAssociationsController.UpdateQmAssociationName
    updateQmAssociationName(id: number, qmAssociation: QmAssociationChange | QmAssociationRequestDto): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.updateQmAssociationName(id, qmAssociation)
            : this.qmAssociationsClient.updateQmAssociationName(id, qmAssociation));
    }

    // ** QmAssociationsController.GetAutoMatchSettingsAsync
    getAutoMatchSettings(id: number): Observable<LegacyQmAssociationAutoMatchSettingsViewModel | QmAssociationAutoMatchSettingsViewModel> {
        return this.invoke(x => x
            ? this.legacy.getAutoMatchSettings(id)
            : this.qmAssociationsClient.getAutoMatchSettings(id));
    }

    // ** QmAssociationsController.UpdateAutoMatchSettingsAsync
    updateAutoMatchSettings(settings: QmAssociationAutoMatchSettingsViewModel | LegacyQmAssociationAutoMatchSettingsViewModel): Observable<void> {
        return this.invoke(x => x
            ? this.legacy.updateAutoMatchSettings(settings as LegacyQmAssociationAutoMatchSettingsViewModel)
            : this.qmAssociationsClient.updateAutoMatchSettings(settings as QmAssociationAutoMatchSettingsViewModel));
    }

    private convertToClob(lob: LineOfBusinessEnum): CategoryLineOfBusinessEnum {
        switch (lob) {
            case LineOfBusinessEnum.Travel: return CategoryLineOfBusinessEnum.Travel;
            case LineOfBusinessEnum.Locums: return CategoryLineOfBusinessEnum.Locums;
            case LineOfBusinessEnum.AccessControl: return CategoryLineOfBusinessEnum.AccessControl;
            default: return null;
        }
    }
}
